/* ===========================================================
   Catálogo de eventos da landing + demo (lexicon Mixpanel) — decisions.md §34.
   Nomes de evento/propriedade vivem AQUI, nunca como strings soltas nas telas.
   Quem instrumenta chama estas funções. Doc legível: docs/analytics-eventos.md
   (manter em sincronia). Propriedades em snake_case; enums em slug estável.
   =========================================================== */
import { track, registrarSuperProps } from "./mixpanel";

export { identifyLead, type LeadIdentity } from "./mixpanel";

/* ---------------- contexto (super properties da sessão) ---------------- */

/** Superfície pública atual — fixa `superficie` (landing|demo) em todos os
 *  eventos seguintes. Chamar ANTES do 1º evento de cada superfície. */
export const marcarSuperficie = (superficie: "landing" | "demo" | "conteudo"): void =>
  registrarSuperProps({ superficie });

/** Potencial estimado do lead (R$/mês recuperável) — fixado quando o diagnóstico
 *  carrega na demo. Vira atributo do perfil/eventos, não um evento "visto". */
export const registrarPotencial = (recuperavelMes: number): void =>
  registrarSuperProps({ recuperavel_mes: recuperavelMes });

/* ---------------- landing (engajamento) ---------------- */

/** Pageview de qualquer rota pública (home `/`, termos, privacidade). */
export const paginaVista = (path: string): void => track("Página Vista", { path });

/** Uma seção da landing entrou no viewport (dispara 1x por seção). */
export const secaoVista = (secao: string): void => track("Seção Vista", { secao });

/** Marco de profundidade de rolagem da página (25 / 50 / 75 / 100%). */
export const scrollAtingido = (pct: number): void =>
  track("Scroll Atingido", { profundidade_pct: pct });

/** Clique em link de âncora interna (#problema, #comecar, #faq, ...). */
export const navClicada = (ancora: string): void => track("Nav Clicada", { ancora });

/** Abertura de um item do FAQ. */
export const faqAberta = (pergunta: string): void => track("FAQ Aberta", { pergunta });

/** Clique em CTA — `cta` identifica a posição (header_signup, hero_signup, ...). */
export const ctaClicado = (cta: string): void => track("CTA Clicado", { cta });

/* ---------------- conteúdo (blog, conceitos, guia, calculadoras) ---------------- */

/** Acesso a uma página de conteúdo (pageview). `tipo` segmenta a leitura:
 *  - acessos no blog  = eventos com tipo ∈ {blog, artigo, conceito, guia}
 *  - acessos na calc. = eventos com tipo = calculadora
 *  `categoria`/`titulo` dão o detalhe (qual post/ferramenta). */
export const conteudoVisto = (p: {
  path: string;
  tipo: "blog" | "artigo" | "conceito" | "guia" | "calculadora";
  categoria?: string;
  titulo?: string;
}): void =>
  track("Conteúdo Visto", {
    path: p.path,
    conteudo_tipo: p.tipo,
    conteudo_categoria: p.categoria,
    conteudo_titulo: p.titulo,
  });

/** O visitante USOU uma calculadora (mexeu em algum campo) — dispara 1x por
 *  carregamento da página. `calc` = qual calculadora. Usuários únicos com este
 *  evento = "quantas pessoas usaram a calculadora". */
export const calculadoraUsada = (p: { calc: string }): void =>
  track("Calculadora Usada", { calc: p.calc });

/* ---------------- demo (funil de validação) ---------------- */

/** Popup do gate aberto (topo do funil da demo). */
export const demoGateAberto = (): void => track("Demo Gate Aberto");

/** Gate concluído (loja + porte). FIXA a persona como super-property — assim todo
 *  o funil seguinte fica segmentável por loja/porte — e marca o evento. `run` =
 *  quantas vezes este visitante já iniciou a demo (mede reincidência). */
export const demoConfigurada = (p: {
  loja: string;
  faixa: string;
  porteFator: number;
  run: number;
}): void => {
  registrarSuperProps({
    loja_nome: p.loja,
    faixa_faturamento: p.faixa,
    porte_fator: p.porteFator,
    demo_run: p.run,
  });
  track("Demo Configurada");
};

/** Intenção de saída DURANTE a demo (mouse deixou a viewport) — máx. 1x/sessão. */
export const exitIntentDisparado = (): void => track("Exit Intent");

/* ---------------- tour guiado (driver.js) ---------------- */

/** Tour iniciado — `total` = nº de passos do roteiro. */
export const tourIniciado = (p: { total: number }): void =>
  track("Tour Iniciado", { tour_total: p.total });

/** Avançou um passo — `passo` global (1..total) + `tela`/`modulo`. */
export const tourPasso = (p: {
  passo: number;
  total: number;
  tela: string;
  modulo: string;
}): void =>
  track("Tour Passo", {
    tour_passo: p.passo,
    tour_total: p.total,
    tour_tela: p.tela,
    tour_modulo: p.modulo,
  });

/** Tour encerrado — `motivo` = `concluiu` | `fechou`; `passo` onde saiu. */
export const tourEncerrado = (p: { concluido: boolean; passo: number; motivo: string }): void =>
  track("Tour Encerrado", {
    tour_concluido: p.concluido,
    tour_passo: p.passo,
    tour_motivo: p.motivo,
  });

/* ---------------- conversão ---------------- */

/** Contato capturado na oferta — `origem` = `tour` | `banner` | `exit_intent`. */
export const contatoCapturado = (p: {
  origem: string;
  intencao: string;
  temWhatsapp: boolean;
  ok: boolean;
}): void =>
  track("Contato Capturado", {
    conversao_origem: p.origem,
    lead_intencao: p.intencao,
    lead_tem_whatsapp: p.temWhatsapp,
    envio_ok: p.ok,
  });

/** Disposição a pagar (sinal de monetização) — `origem` da conversão. */
export const queroAssinar = (p: { origem: string }): void =>
  track("Quero Assinar", { conversao_origem: p.origem });

/** Inscrição na newsletter (topo de funil, form do blog/conteúdo). `origem` = path
 *  da página onde inscreveu; `ok` = a Function aceitou o envio (best-effort). */
export const newsletterInscrito = (p: { origem: string; ok: boolean }): void =>
  track("Newsletter Inscrita", { conversao_origem: p.origem, envio_ok: p.ok });
